// Website traffic — the staff view of the website's anonymous counter (G-AIW-14, decided by Jacky
// 12 October 2026: "Use a privacy-friendly counter"; owner doc docs/WEBSITE-ANALYTICS.md).
// Page totals only: daily page views, top pages, where visitors came from, devices, and the website
// actions (bookings submitted, self-quotes carried, member links). Nothing here identifies anyone.
// EmmA reads the same counter with the published read `website_traffic`.
//
// GET /api/web-traffic is a statement route (the typed Hono chain is at TS's instantiation-depth
// limit), so it is read through fetchImpl like the other late routes.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { shiftSydneyDay, sydneyToday } from "@artemis-group/aiw-contracts";
import { cn } from "@aiw/ui";
import { authHeaders, fetchImpl } from "../lib/api";
import { env } from "../lib/env";
import { useT } from "../lib/i18n";
import { Panel } from "../shell/Page";

type Count = { key: string | null; count: number };
export type WebTraffic = { from: string; to: string; page_views: number; days: { day: string; page_views: number }[]; top_pages: Count[]; top_referrers: Count[]; devices: Count[]; events: Count[] };

const RANGES = [7, 30, 90] as const;
type Range = (typeof RANGES)[number];
const DEVICE_IDS = { phone: "webTraffic.device.phone", tablet: "webTraffic.device.tablet", desktop: "webTraffic.device.desktop" } as const;
const EVENT_IDS = { book_submitted: "webTraffic.event.book_submitted", self_quote_carried: "webTraffic.event.self_quote_carried", member_link_requested: "webTraffic.event.member_link_requested" } as const;

export function useWebTraffic(days: Range) {
  return useQuery({
    queryKey: ["web-traffic", days],
    staleTime: 60_000,
    queryFn: async () => {
      const to = sydneyToday();
      const from = shiftSydneyDay(to, -(days - 1));
      const r = await fetchImpl(`${env.apiBase}/api/web-traffic?from=${from}&to=${to}`, { headers: await authHeaders() });
      const b = (await r.json().catch(() => ({}))) as { data?: WebTraffic; error?: string };
      if (!r.ok || !b.data) throw new Error(b.error ?? `HTTP ${r.status}`);
      return b.data;
    },
  });
}

export function WebsiteTraffic() {
  const { t } = useT();
  const [days, setDays] = useState<Range>(7);
  const q = useWebTraffic(days);
  const d = q.data;
  const label = (r: Range) => (r === 7 ? t('webTraffic.range.7') : r === 30 ? t('webTraffic.range.30') : t('webTraffic.range.90'));
  const deviceLabel = (k: string | null) => (k && k in DEVICE_IDS ? t(DEVICE_IDS[k as keyof typeof DEVICE_IDS]) : t('webTraffic.device.unknown'));
  const eventLabel = (k: string | null) => (k && k in EVENT_IDS ? t(EVENT_IDS[k as keyof typeof EVENT_IDS]) : String(k));

  return (
    <Panel
      title={t('webTraffic.title')}
      meta={
        <span className="inline-flex items-center gap-2">
          <span className="hidden sm:inline">{t('webTraffic.meta')}</span>
          <span className="inline-flex rounded-md border p-0.5">
            {RANGES.map((r) => (
              <button key={r} type="button" onClick={() => setDays(r)} className={cn("rounded px-2 text-micro", r === days ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}>{label(r)}</button>
            ))}
          </span>
        </span>
      }
    >
      {q.isLoading && <p className="text-sm text-muted-foreground">{t('webTraffic.loading')}</p>}
      {q.isError && <p className="text-sm text-destructive">{t('webTraffic.failed', { reason: q.error instanceof Error ? q.error.message : "?" })}</p>}
      {d && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-x-6 gap-y-2">
            <div>
              <div className="num font-display text-2xl font-semibold">{d.page_views.toLocaleString("en-AU")}</div>
              <div className="text-sm font-medium">{t('webTraffic.pageViews')}</div>
              <div className="text-micro text-muted-foreground">{t('webTraffic.perDay', { count: (Math.round((d.page_views / Math.max(1, d.days.length)) * 10) / 10).toLocaleString("en-AU") })}</div>
            </div>
            <DailyBars days={d.days} label={t('webTraffic.daily')} />
          </div>
          {d.page_views === 0 ? <p className="text-sm text-muted-foreground">{t('webTraffic.none')}</p> : (
            <div className="grid gap-4 md:grid-cols-2">
              <Ranked title={t('webTraffic.topPages')} rows={d.top_pages} name={(k) => k ?? "—"} mono />
              <Ranked title={t('webTraffic.topReferrers')} rows={d.top_referrers} name={(k) => k ?? t('webTraffic.direct')} />
              <Ranked title={t('webTraffic.devices')} rows={d.devices} name={deviceLabel} />
              {d.events.length > 0 && <Ranked title={t('webTraffic.actions')} rows={d.events} name={eventLabel} />}
            </div>
          )}
          <p className="text-micro text-muted-foreground">{t('webTraffic.footnote')}</p>
        </div>
      )}
    </Panel>
  );
}

function Ranked({ title, rows, name, mono }: { title: string; rows: Count[]; name: (k: string | null) => string; mono?: boolean }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div>
      <h4 className="mb-1 font-mono text-micro uppercase text-muted-foreground">{title}</h4>
      <ul className="space-y-1 text-sm">
        {rows.map((r) => (
          <li key={String(r.key)} className="grid grid-cols-[minmax(0,1fr)_5rem_auto] items-center gap-2">
            <span className={cn("truncate", mono && "font-mono text-micro")} title={name(r.key)}>{name(r.key)}</span>
            <span className="h-2 rounded-sm bg-muted"><span className="block h-2 rounded-sm bg-primary" style={{ width: `${Math.round((r.count / max) * 100)}%` }} /></span>
            <span className="num text-micro">{r.count.toLocaleString("en-AU")}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Page views per Sydney day: one bar each, the day and its count on hover. */
function DailyBars({ days, label }: { days: { day: string; page_views: number }[]; label: string }) {
  const max = Math.max(1, ...days.map((d) => d.page_views));
  const w = 320, h = 56, gap = days.length > 40 ? 1 : 2;
  const bw = Math.max(1, (w - gap * (days.length - 1)) / Math.max(1, days.length));
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-14 w-full max-w-sm flex-1" role="img" aria-label={label}>
      {days.map((d, i) => {
        const bh = d.page_views === 0 ? 1 : Math.max(2, Math.round((d.page_views / max) * (h - 2)));
        return (
          <rect key={d.day} x={i * (bw + gap)} y={h - bh} width={bw} height={bh} rx={1} className={d.page_views === 0 ? "fill-muted" : "fill-primary"}>
            <title>{`${new Date(`${d.day}T12:00:00Z`).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", timeZone: "Australia/Sydney" })} · ${d.page_views}`}</title>
          </rect>
        );
      })}
    </svg>
  );
}
